import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  combineLatest,
  map,
  Observable,
  Subject,
  Subscription,
  takeUntil,
} from 'rxjs';
import { Store } from '@ngrx/store';
import { PermissionsService } from 'src/app/core/services/permissions/permission.service';
import {
  GroupStatus,
  TaggedGroup,
  TaggedGroups,
  TagItemVendorService,
} from '../tag-item-vendor.service';
import { CollectionViewer, DataSource } from '@angular/cdk/collections';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import {
  ExtendedKeycloakUser,
  IItemToRequest,
  MyPurchaseForm,
  StoreDropdowns,
  UserInformation,
} from 'src/app/shared/models/app-models';
import _ from 'lodash';
import { ActivatedRoute, Router } from '@angular/router';
import SourcingStatusJSON from 'src/app/data/constants/SourcingStatus.json';
import { MatDialog } from '@angular/material/dialog';
import {
  TaggingUpdateComponent,
  TaggingUpdateDialogData,
} from '../tagging-update/tagging-update.component';
import { MatSort } from '@angular/material/sort';
import { RFSReady, RfsService } from '../rfs.service';
import { RFSReadyRole } from 'src/app/shared/models/database-models/RFS.model';

interface ItemSourcingItem {
  itemNumber: number;
  itemDescription?: string;
  vendorTagged?: string;
  sourcingStatus?: string;
  purchaseExecutiveName: string;
}

interface TaggedItemVendorItem extends TaggedGroup {
  itemNumberSet: string;
  vendorSet: string;
  sourcingStatus: string;
  purchaseExecutiveName: string;
  taggedGroup: TaggedGroup;
}

@Component({
  selector: 'app-tagging-list',
  templateUrl: './tagging-list.component.html',
  styleUrls: ['./tagging-list.component.scss'],
})
export class TaggingListComponent implements OnInit, OnDestroy {
  @Input() isLocked: boolean = true
  isReadOnly: boolean = false;
  isAssignedPEAndNotReadied = false;
  permissionData$: Observable<any>;
  destroy$: Subject<boolean> = new Subject<boolean>();
  form: MyPurchaseForm | null = null;
  userList: ExtendedKeycloakUser[] = [];
  purchaseType: string = '';
  public loggedInUsername?: string;
  private dialogSubscription?: Subscription;
  itemSourcingDataSource: MatTableDataSource<ItemSourcingItem> =
    new MatTableDataSource<ItemSourcingItem>([]);
  itemSourcingDisplayColumns = [
    'itemNumber',
    'itemDescription',
    'vendorTagged',
    'purchaseExecutiveName',
    'sourcingStatus',
  ];
  taggedItemVendorDataSource: MatTableDataSource<TaggedItemVendorItem> =
    new MatTableDataSource<TaggedItemVendorItem>([]);
  taggedItemVendorDisplayColumns = [
    'purchaseExecutiveName',
    'itemNumberSet',
    'vendorSet',
    'sourcingStatus',
    'action',
  ];

  private itemSourcingPaginator!: MatPaginator;
  private taggedItemVendorPaginator!: MatPaginator;
  pageSizeUniqueKeyItemSourcing = 'pageSize-ItemVendorList-itemSourcing';
  pageSizeUniqueKeyTaggedItemVendor =
    'pageSize-ItemVendorList-taggedItemVendor';
  defaultItemSourcingPageSize = 5;
  defaultTaggedItemVendorPageSize = 5;
  pageSizeOptions: number[] = [5, 10, 20];

  @ViewChild('itemSourcingPaginator', { read: MatPaginator })
  set matItemSourcingPaginator(mp: MatPaginator) {
    this.itemSourcingPaginator = mp;
    this.itemSourcingDataSource.paginator = this.itemSourcingPaginator;
  }
  @ViewChild('taggedItemVendorPaginator', { read: MatPaginator })
  set matTaggedItemVendorPaginator(mp: MatPaginator) {
    this.taggedItemVendorPaginator = mp;
    this.taggedItemVendorDataSource.paginator = this.taggedItemVendorPaginator;
  }

  private itemSourcingSort!: MatSort;
  @ViewChild('itemSourcingTableSort', { read: MatSort })
  set matItemSourcingSort(ms: MatSort) {
    this.itemSourcingSort = ms;
    this.itemSourcingDataSource.sort = this.itemSourcingSort;
  }

  private taggedItemVendorSort!: MatSort;
  @ViewChild('taggedItemVendorTableSort', { read: MatSort })
  set matTaggedItemVendorSort(ms: MatSort) {
    this.taggedItemVendorSort = ms;
    this.taggedItemVendorDataSource.sort = this.taggedItemVendorSort;
  }

  constructor(
    private permissionService: PermissionsService,
    private router: Router,
    private route: ActivatedRoute,
    private store: Store<{
      user: UserInformation;
      dp: StoreDropdowns;
      form: MyPurchaseForm;
    }>,
    public tagItemVendorService: TagItemVendorService,
    private rfsService: RfsService,
    private taggingUpdateDialog: MatDialog
  ) {
    // Restore any stored page size selected previously
    const storedItemSourcingPageSize = localStorage.getItem(
      this.pageSizeUniqueKeyItemSourcing
    );
    if (storedItemSourcingPageSize)
      this.defaultItemSourcingPageSize = Number(storedItemSourcingPageSize);
    const storedTaggedItemVendorPageSize = localStorage.getItem(
      this.pageSizeUniqueKeyTaggedItemVendor
    );
    if (storedTaggedItemVendorPageSize)
      this.defaultTaggedItemVendorPageSize = Number(
        storedTaggedItemVendorPageSize
      );

    this.permissionData$ = this.permissionService
      .getService()
      .pipe(takeUntil(this.destroy$));

    combineLatest([
      this.store.select('form'),
      this.tagItemVendorService.taggedGroups$,
      this.store.select('dp'),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        ([form, taggedGroupsObj, dp]: [
          MyPurchaseForm,
          TaggedGroups,
          StoreDropdowns
        ]) => {
          if (
            form.documentNo &&
            form.documentNo === taggedGroupsObj.MPDocumentNumber
          ) {
            const { taggedGroups } = taggedGroupsObj;

            this.itemSourcingDataSource.data = this.formatItemSourcingList(
              form.ItemList,
              taggedGroups,
              dp.UserList as ExtendedKeycloakUser[]
            );

            const filteredTaggedGroups =
              this.tagItemVendorService.getActiveTaggedGroups(taggedGroups);

            this.taggedItemVendorDataSource.data =
              this.formatTaggedItemVendorList(
                filteredTaggedGroups,
                dp.UserList as ExtendedKeycloakUser[]
              );
          }
        }
      );

    combineLatest([this.store.select('form'), this.rfsService.rfsReady$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([form, RFSReady]: [MyPurchaseForm, RFSReady]) => {
        // Document number changed
        if (this.form && this.form.documentNo !== form.documentNo) {
          this.isAssignedPEAndNotReadied = false;
        }

        // RFSReady data came in
        if (
          form.documentNo &&
          RFSReady.data &&
          form.documentNo === RFSReady.data.MPDocumentNumber
        ) {
          this.isAssignedPEAndNotReadied = false;

          const { roleId, isRFSReadied } = RFSReady.data;

          if (roleId && roleId === RFSReadyRole.ASSIGNED_PE && !isRFSReadied) {
            this.isAssignedPEAndNotReadied = true;
          }
        }
      });

    this.store
      .select('form')
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (form: MyPurchaseForm) => {
        this.form = form;
      });
    this.store
      .select('user')
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (user: UserInformation) => {
        this.loggedInUsername = user.userName;
      });

    this.purchaseType = this.route.snapshot.params['purchaseType'].split(
      ';'
    )[0] as string;
  }

  ngOnInit(): void {}
  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();

    if (this.dialogSubscription) {
      this.dialogSubscription.unsubscribe();
    }
  }

  private formatTaggedItemVendorList(
    taggedGroups: TaggedGroups['taggedGroups'],
    userList: ExtendedKeycloakUser[]
  ) {
    return taggedGroups.map((taggedGroup) => {
      const vendorNames = taggedGroup.vendors?.map(
        (vendor) => vendor.vendorName
      );
      const purchaseExecutiveName =
        userList.find((user) => user.id === taggedGroup.purchaseExecutive)
          ?.last_name ?? '-';
      const formattedItem: TaggedItemVendorItem = {
        ...taggedGroup,
        itemNumberSet: taggedGroup.itemNumbers.length > 0 ? taggedGroup.itemNumbers.join(', ') : 'Not yet selected',
        vendorSet: vendorNames
          ? vendorNames.length > 0
            ? vendorNames.join(', ')
            : 'Not yet selected'
          : '-',
        sourcingStatus: taggedGroup.sourcingStatusId
          ? this.getSourcingStatusNameFromId(taggedGroup.sourcingStatusId)
          : '-',
        purchaseExecutiveName: purchaseExecutiveName,
        taggedGroup: taggedGroup,
      };
      return formattedItem;
    });
  }

  private formatItemSourcingList(
    itemList: IItemToRequest[],
    taggedGroups: TaggedGroups['taggedGroups'],
    userList: ExtendedKeycloakUser[]
  ) {
    const activeTaggedGroups =
      this.tagItemVendorService.getActiveTaggedGroups(taggedGroups);

    const itemSourcingList = itemList
      .filter((item) => {
        const { isDeleted, Strikethrough } = item;
        return !isDeleted && !Strikethrough;
      })
      .map((item) => {
        const {
          displayItemNumber,
          ItemDescription,
          PurchaseExecutive,
        } = item.AddItems;

        let SourcingStatusId = item.AddItems.SourcingStatusId;

        let vendorNames: string[] | null = []

        activeTaggedGroups.forEach((group) => {
          const {itemNumbers, vendors} = group;

          if (itemNumbers.includes(displayItemNumber)) {

            if (!vendors) {
              vendorNames = null
            } else if (Array.isArray(vendors) && Array.isArray(vendorNames)) {
              vendorNames.push(...vendors.map(vendor => vendor.vendorName))
            }
          }

          // if SourcingStatusId is missing from items, check from taggedGroups
          if(!SourcingStatusId && group.sourcingStatusId && group.itemNumbers.includes(item.AddItems.itemId)) {
            SourcingStatusId = group.sourcingStatusId;
          }
        })

        const uniqueVendorNames = Array.isArray(vendorNames) ? Array.from(new Set(vendorNames)) : null;

        const purchaseExecutiveName =
          userList.find((user) => {
            return user.id === PurchaseExecutive;
          })?.last_name ?? '-';

        const formattedItem: ItemSourcingItem = {
          itemNumber: displayItemNumber,
          itemDescription: ItemDescription ?? '-',
          vendorTagged: uniqueVendorNames
            ? uniqueVendorNames.length > 0
              ? _.sortBy(uniqueVendorNames).join(', ')
              : 'Not yet selected'
            : '-',
          sourcingStatus: SourcingStatusId
            ? this.getSourcingStatusNameFromId(SourcingStatusId)
            : '-',
          purchaseExecutiveName: purchaseExecutiveName,
        };

        return formattedItem;
      });
      
    return itemSourcingList;
  }

  public onTagItemVendorButtonClick() {
    // Reset data for item-vendor-add page, start from blank
    this.tagItemVendorService.resetTempTagGroup();

    this.router.navigate([
      '/purchase/',
      this.purchaseType,
      this.form?.documentNo,
      'tag-item-vendor',
    ]);
  }

  public onEditTaggedGroupClick(taggedGroup: TaggedGroup) {
    // Load selected taggedGroup into tempTagGroup
    this.tagItemVendorService.setTempTagGroup(taggedGroup);

    // Open update dialog
    if (this.dialogSubscription) {
      this.dialogSubscription.unsubscribe();
    }

    const dialogData: TaggingUpdateDialogData = {
      existingTaggedGroupRef: _.cloneDeep(taggedGroup),
    };

    const dialogRef = this.taggingUpdateDialog.open(TaggingUpdateComponent, {
      width: `${window.innerWidth - 150}px`,
      minHeight: `${window.innerHeight - 50}px`,
      data: dialogData,
    });

    this.dialogSubscription = dialogRef.afterClosed().subscribe(() => {
      // Observable will auto update list data
    });
  }

  public onItemSourcingPaginatorPage(event: PageEvent) {
    const { pageSize } = event;

    localStorage.setItem(
      this.pageSizeUniqueKeyItemSourcing,
      pageSize.toString()
    );
  }

  public onTaggedItemVendorPaginatorPage(event: PageEvent) {
    const { pageSize } = event;

    localStorage.setItem(
      this.pageSizeUniqueKeyTaggedItemVendor,
      pageSize.toString()
    );
  }

  applyItemSourcingFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.itemSourcingDataSource.filter = filterValue.trim().toLowerCase();

    if (this.itemSourcingDataSource.paginator) {
      this.itemSourcingDataSource.paginator.firstPage();
    }
  }

  applyTaggedItemVendorFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.taggedItemVendorDataSource.filter = filterValue.trim().toLowerCase();

    if (this.taggedItemVendorDataSource.paginator) {
      this.taggedItemVendorDataSource.paginator.firstPage();
    }
  }

  public getSourcingStatusNameFromId(sourcingStatusId: number) {
    const foundSourcingStatus = SourcingStatusJSON.find(
      (entry) => entry.SourcingStatusId === sourcingStatusId
    );

    return foundSourcingStatus ? foundSourcingStatus.name : '';
  }
}
